export const CONSENT_COOKIE_NAME = "PFCONSENT";
export const DEPRECATED_CONSENT_COOKIE_NAME = "tarteaucitron";
/**
 * Retrieves the value of a specific cookie by name.
 * @param name The name of the cookie to retrieve.
 * @returns The cookie's value, or undefined if not found.
 */
export const getCookie = (name: string): string | undefined => {
    if (typeof document === "undefined") {
        return undefined;
    }
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return undefined;
};

/**
 * Sets a cookie with a given name, value, and expiration days.
 * @param name The name of the cookie.
 * @param value The value of the cookie.
 * @param days The number of days until the cookie expires.
 */
export const setCookie = (name: string, value: string, days: number) => {
    let expires = "";
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
    }

    // To ensure the cookie is accessible across all subdomains, we set it on the top-level domain.
    // For example, for 'app.example.com', the cookie domain will be '.example.com'.
    // This logic avoids setting a domain for localhost or IP addresses, as it's not applicable.
    let domainAttribute = "";
    if (typeof window !== "undefined" && window.location.hostname) {
        const hostname = window.location.hostname;
        // Do not set domain for IP addresses or localhost.
        // A simple check is to see if the hostname contains any letters.
        const isActualDomain = /[a-zA-Z]/.test(hostname);
        if (isActualDomain) {
            const hostnameParts = hostname.split(".");
            domainAttribute = "; domain=." + hostnameParts.slice(-2).join(".");
        }
    }
    document.cookie = `${name}=${value || ""}${expires}; path=/${domainAttribute}; SameSite=Lax`;
};

/**
 * Parses a consent cookie string (e.g., "!serviceId=value!serviceId=value") into an object.
 * @param cookieString The raw cookie string.
 * @returns An object mapping service IDs to their consent state ('true', 'false', or 'wait').
 */
export const parseConsentCookieString = (
    cookieString: string | undefined
): Record<string, "true" | "false" | "wait"> => {
    const consents: Record<string, "true" | "false" | "wait"> = {};
    if (!cookieString) return consents;
    cookieString
        .split("!")
        .filter(Boolean)
        .forEach(part => {
            const [id, value] = part.split("=");
            if (id && (value === "true" || value === "false" || value === "wait"))
                consents[id] = value;
        });
    return consents;
};

export const getCookieConsent = (serviceName: string): boolean | undefined => {
    const cookieString = getCookie(CONSENT_COOKIE_NAME);
    const consents = parseConsentCookieString(cookieString);
    const consentValue = consents[serviceName];
    if (consentValue === "true") {
        return true;
    }
    if (consentValue === "false") {
        return false;
    }
    return undefined;
};

export const deleteOldConsentCookie = () => {
    if (!getCookie(DEPRECATED_CONSENT_COOKIE_NAME)) {
        return;
    }
    deleteCookie(DEPRECATED_CONSENT_COOKIE_NAME);
};

export const deleteCookie = (name: string) => {
    // To delete a cookie, we set it with an expiration date in the past.
    // We try both the exact hostname and the top-level domain variant since we
    // cannot read back which domain the cookie was originally set on.
    const domainVariants: string[] = [];
    if (typeof window !== "undefined" && window.location.hostname) {
        const hostname = window.location.hostname;
        const isActualDomain = /[a-zA-Z]/.test(hostname);
        if (isActualDomain) {
            domainVariants.push(`; domain=${hostname}`);
            const topLevelDomain =
                "." +
                hostname
                    .split(".")
                    .slice(-2)
                    .join(".");
            if (topLevelDomain !== hostname) {
                domainVariants.push(`; domain=${topLevelDomain}`);
            }
        }
    }

    domainVariants.forEach(domainAttribute => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttribute}; SameSite=Lax`;
    });
};
